/**
 * JourneyGameScreen - Main gameplay screen
 */

import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { redact } from '../../../../engine/language.js';
import './JourneyGameScreen.css';

// Import scene components
import BalloonScene from '../scenes/BalloonScene';
import FruitScene from '../scenes/FruitScene';
import RocketScene from '../scenes/RocketScene';
import RaceScene from '../scenes/RaceScene';
import AnimalScene from '../scenes/AnimalScene';
import SandboxScene from '../scenes/SandboxScene';
import KitchenScene from '../scenes/KitchenScene';

const SCENES = {
  balloon: BalloonScene,
  fruit: FruitScene,
  rocket: RocketScene,
  race: RaceScene,
  animal: AnimalScene,
  sandbox: SandboxScene,
  kitchen: KitchenScene,
};

const JourneyGameScreen = forwardRef(({
  level,
  phase,
  progress,
  isListening,
  transcript,
  isComplete,
  lastPraise,
  onOpenMap,
  onSkip,
  onToggleMic,
  isSpeaking,
}, ref) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [heardText, setHeardText] = useState('');

  // Get scene component
  const SceneComponent = SCENES[level?.scene] || SCENES.kitchen;

  // Update heard text
  useEffect(() => {
    if (transcript) {
      // Never the raw result: see engine/language.js.
      setHeardText(redact(transcript));
    }
  }, [transcript]);

  // Reset on new level
  useEffect(() => {
    setShowConfetti(false);
    setHeardText('');
  }, [level?.id]);

  // Expose celebrate method
  useImperativeHandle(ref, () => ({
    celebrate: () => {
      setShowConfetti(true);
    }
  }));

  if (!level) return null;

  return (
    <div className="journey-game">
      {/* Scene area */}
      <div className="journey-game__scene">
        <SceneComponent
          level={level}
          isComplete={isComplete}
          isListening={isListening}
        />

        {/* Confetti */}
        {showConfetti && (
          <div className="journey-game__confetti">
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className="journey-game__confetti-piece"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  backgroundColor: ['#22c55e', '#fbbf24', '#38bdf8', '#f43f5e', '#8b5cf6'][Math.floor(Math.random() * 5)],
                }}
              />
            ))}
          </div>
        )}

        {/* HUD */}
        <div className="journey-game__hud">
          <div className="journey-game__hud-left">
            <div className="journey-game__phase-badge" style={{ background: phase?.color }}>
              {phase?.icon}
            </div>
          </div>

          <div className="journey-game__hud-center">
            <div className="journey-game__progress">
              <div 
                className="journey-game__progress-fill"
                style={{ width: `${progress?.percent || 0}%` }}
              />
            </div>
          </div>

          <div className="journey-game__hud-right">
            <span className="journey-game__level-badge">
              Level {level.id}
            </span>
            <button 
              className="journey-game__map-btn"
              onClick={onOpenMap}
              aria-label="Open map"
            >
              🗺️
            </button>
          </div>
        </div>

        {/* Success feedback */}
        {isComplete && (
          <div className="journey-game__success">
            {lastPraise}
          </div>
        )}
      </div>

      {/* Controls area */}
      <div className="journey-game__controls">
        {/* Prompt */}
        <div className="journey-game__prompt">
          <div className="journey-game__prompt-label">
            {level.target ? 'Say this:' : 'Say anything!'}
          </div>
          <div className="journey-game__prompt-word">
            {level.target?.toUpperCase() || '???'}
          </div>
          <div className="journey-game__prompt-hint">
            {level.instruction}
          </div>
        </div>

        {/* Mic button */}
        <div className="journey-game__mic-container">
          <button
            className={`journey-game__mic ${isListening ? 'journey-game__mic--active' : ''}`}
            onClick={onToggleMic}
            disabled={isComplete}
          >
            <span className="journey-game__mic-icon">
              {isListening ? '🎤' : '🔇'}
            </span>
            {isListening && <span className="journey-game__mic-pulse" />}
          </button>
        </div>

        {/* Transcript */}
        <div className="journey-game__transcript">
          {heardText ? (
            <span>Heard: "{heardText}"</span>
          ) : isListening ? (
            <span className="journey-game__transcript--listening">Listening...</span>
          ) : (
            <span>Tap mic to speak</span>
          )}
        </div>

        {/* Dev skip button */}
        {onSkip && (
          <button className="journey-game__skip" onClick={onSkip}>
            Skip →
          </button>
        )}
      </div>
    </div>
  );
});

JourneyGameScreen.displayName = 'JourneyGameScreen';

export default JourneyGameScreen;
